import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type SignerResponseDto,
  SignerRole,
  type SigningApiDataType,
} from 'xosign-types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { type UploadedFile } from '@/types/document';
import useApi from '@/hooks/useApi';
import { stringToBigInt } from '@/utils/bigInt';
import SuspenseFallback from '@/components/ModalSidebar/SuspenseFallback';
import useRedirectToLogin from '@/hooks/useRedirectToLogin';

type SignContextType = {
  canSignDocument: boolean;
  documents: UploadedFile[];
  loadAllData: () => void;
  loginInfo: {
    needed: boolean;
    timestamp: Date | null;
    valid: () => boolean;
    loginType: 'sms' | 'password' | 'pin' | null;
  };
  overallLoading: boolean;
  setDocuments: Dispatch<SetStateAction<UploadedFile[]>>;
  setLoginSuccessful: () => void;
  setLoginTimestamp: Dispatch<SetStateAction<Date | null>>;
  setRequireLogin: Dispatch<SetStateAction<boolean>>;
  signerState: string;
  signers: SignerResponseDto[];
  signing?: SigningApiDataType;
  visitor?: SignerResponseDto;
};

const SignContext = createContext<SignContextType | undefined>(undefined);

export const SignProvider = ({ children }: { children: ReactNode }) => {
  const { signerId, workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [getMe, getMeData] = useApi('users.me');
  const redirectToLogin = useRedirectToLogin();

  const [signing, setSigning] = useState<SigningApiDataType>();
  // We need to store the documents separately to add FE object keys
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [signers, setSigners] = useState<SignerResponseDto[]>([]);
  const [visitor, setVisitor] = useState<SignerResponseDto>();
  const [requireLogin, setRequireLogin] = useState<boolean>(false);
  const [requiredLoginType, setRequiredLoginType] = useState<
    'password' | 'pin' | 'sms' | null
  >(null);
  const [token] = useState<string | null>(
    searchParams.get('key') ?? sessionStorage.getItem('token')
  );
  // Capture the absolute sign-page URL once, at mount. We must NOT rebuild the
  // login `continue` target from `window.location.href` inside `loadAllData`,
  // because by the time a redirect fires (or the effect re-runs) the location may
  // already be `/auth/login`, which would drop the continue param entirely.
  const [signContinueUrl] = useState<string>(
    `${window.location.origin}/sign/${workflowId}/${signerId}/welcome?key=${token}&postLogin=true`
  );
  // Once we decide to bounce to login, don't let a second pass redirect again
  // (which would clobber the `continue` param built above).
  const redirectingToLoginRef = useRef(false);
  const [signerState, setSignerState] = useState<string>('LOAD-DATA');
  const [canSignDocument, setCanSignDocument] = useState<boolean>(false);
  const [loginTimestamp, setLoginTimestamp] = useState<Date | null>(null);

  const [loadSigning, { loading: loadingSigning }] = useApi('sign.get');
  const [loadSigners, { loading: loadingSigners }] = useApi('sign.signers.get');

  const checkIfLoginIsValid = () => {
    if (loginTimestamp === null) {
      return false;
    }

    if (dayjs(loginTimestamp).diff(dayjs(), 'minutes') > 30) {
      return false;
    }

    // Renew timestamp sometimes
    if (dayjs(loginTimestamp).diff(dayjs(), 'minutes') > 1) {
      setLoginTimestamp(new Date());
    }

    return true;
  };

  // Single entry point for bouncing to login from the sign flow: clears any
  // tokens, uses the stable continue URL captured at mount, and guards against a
  // second pass redirecting again (which would drop the continue param).
  const bounceToLogin = () => {
    if (redirectingToLoginRef.current) {
      return;
    }
    redirectingToLoginRef.current = true;
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    redirectToLogin(true, signContinueUrl);
  };

  const loadAllData = async (meEmailOverride?: string | null) => {
    if (workflowId && token) {
      const meEmail =
        meEmailOverride !== undefined
          ? meEmailOverride
          : (getMeData.response?.data.email ?? null);

      const localStorageToken = localStorage.getItem('token');
      const appUserLoggedIn = !!meEmail && !!localStorageToken;

      // Probe with the signer `?key=` token first: only it can read this signer's
      // own (unmasked) email, which we need to detect a "different user is logged
      // in" mismatch. Signer-facing routes are guarded by
      // `requireLoginIfSignerTrustedMiddleware`, so a *known* signer (account
      // linked) gets a 401 `ETrustedSignerMustLogin` here.
      sessionStorage.setItem('token', token);

      const signerProbe = await loadSigners({ workflowId });

      let isKnownSignerLogin = false;
      if (signerProbe?.error?.statusCode === 401) {
        isKnownSignerLogin =
          signerProbe.error.response.code === 'ETrustedSignerMustLogin';
      }

      if (isKnownSignerLogin && !appUserLoggedIn) {
        // Known signer with no app session → force login before anything loads.
        bounceToLogin();
        return;
      }

      // Known signer AND an app user is logged in: the signer token alone can't
      // tell us whether the right user is logged in, so re-probe as the app user.
      // If they ARE this signer the call succeeds and we keep the app token; if not
      // (404) the different-user check below catches it.
      if (isKnownSignerLogin && appUserLoggedIn) {
        sessionStorage.setItem('token', localStorageToken);
      }

      const signersResponse = isKnownSignerLogin
        ? await loadSigners({ workflowId })
        : signerProbe;

      const signer = signersResponse?.response?.data.find(
        (signer) => signer.id === signerId
      );

      // Force an account switch ONLY for a *known* (account-linked) signer that the
      // logged-in user is not. An UNKNOWN signer link is meant to be signed as a
      // guest, so a logged-in different user must be allowed through without a
      // bounce — `isKnownSignerLogin` gates this. For a known signer we detect the
      // mismatch via the app-user re-probe not finding this signer (no `signer`) or
      // an email mismatch.
      const differentUserLoggedIn =
        appUserLoggedIn &&
        isKnownSignerLogin &&
        (!signer ||
          (!!signer.email &&
            signer.email.toLowerCase() !== meEmail.toLowerCase()));

      if (differentUserLoggedIn) {
        bounceToLogin();
        return;
      }

      // The logged-in app user IS this signer: upgrade to their app token so the
      // signing call authenticates as the user (enables the password/enhanced-
      // security path and the fresh-login bypass).
      const signerIsAppUser =
        !!meEmail &&
        !!signer?.email &&
        signer.email.toLowerCase() === meEmail.toLowerCase();

      if (signerIsAppUser && localStorageToken) {
        sessionStorage.setItem('token', localStorageToken);
      }

      const signingResponse = await loadSigning({ workflowId });

      if (signingResponse?.error?.statusCode === 401) {
        if (signingResponse.error.response.code === 'ETrustedSignerMustLogin') {
          bounceToLogin();
          return;
        }
        if (
          signingResponse.error.response.code ===
          'EEnhancedSecurityPasswordRequired'
        ) {
          setRequireLogin(true);
          setRequiredLoginType('password');
        }
        if (
          signingResponse.error.response.code ===
          'EEnhancedSecuritySMSPinRequired'
        ) {
          setRequireLogin(true);
          setRequiredLoginType('sms');
        }
        if (
          signingResponse.error.response.code === 'EEnhancedSecurityPinRequired'
        ) {
          setRequireLogin(true);
          setRequiredLoginType('pin');
        }
      }

      if (
        signingResponse?.response?.success &&
        signersResponse?.response?.success
      ) {
        const signingData = signingResponse.response.data;
        setSigning(signingData);

        setDocuments(
          signingData.documents.map((document) => ({
            id: document.id,
            progress: 100,
            status: 'success' as const,
            fileMeta: {
              name: document.filename,
              size: stringToBigInt(document.byteSize),
              pageCount: document.pageCount,
            },
          }))
        );

        const signersData = signersResponse.response.data;
        setSigners(signersData);

        if (signersData.length > 0) {
          const foundVisitor = signersData.find(
            (signer) => signer.id === signerId
          );

          if (foundVisitor) {
            setVisitor(foundVisitor);
            setSignerState(foundVisitor.state);

            setCanSignDocument(
              foundVisitor.role === SignerRole.SIGNER &&
                (foundVisitor.state === 'PENDING' ||
                  foundVisitor.state === 'SIGNING' ||
                  foundVisitor.state === 'EXPIRED')
            );
          }
        }
      }
    }
  };

  const setLoginSuccessful = () => {
    setVisitor((prevState) => {
      return { ...prevState, needsLogin: false } as SignerResponseDto;
    });
    setRequireLogin(false);
    void loadAllData();
  };

  // `loadAllData` is a fresh closure each render and would re-fire this
  // effect into an infinite loop if listed as a dep. Read it through a ref so
  // the effect only re-runs when the actual identifiers change.
  const loadAllDataRef = useRef(loadAllData);
  useEffect(() => {
    loadAllDataRef.current = loadAllData;
  });

  useEffect(() => {
    void (async () => {
      const meResult = await getMe();
      if (!workflowId || !token) {
        void navigate('/');
        return;
      }
      await loadAllDataRef.current(meResult?.response?.data.email ?? null);
    })();
  }, [getMe, navigate, token, workflowId]);

  const contextValue = {
    canSignDocument,
    documents,
    loadAllData,
    loginInfo: {
      needed: requireLogin,
      loginType: requiredLoginType,
      timestamp: loginTimestamp,
      valid: checkIfLoginIsValid,
    },
    setRequireLogin,
    overallLoading: !!loadingSigning || !!loadingSigners,
    setDocuments,
    setLoginSuccessful,
    setLoginTimestamp,
    signerState,
    signers,
    signing,
    visitor,
  };

  if (getMeData.loading) {
    return <SuspenseFallback />;
  }

  return (
    <SignContext.Provider value={contextValue}>{children}</SignContext.Provider>
  );
};

const useSign = (): SignContextType => {
  const ctx = useContext(SignContext);

  if (!ctx) {
    throw new Error('useSign must be used within SignProvider');
  }

  return ctx;
};

export default useSign;
